import { useState } from 'react';
import { MapPin, Clock, ChevronDown, ChevronUp, CreditCard, Droplets, ShoppingCart, Target, Car, Apple, Wrench, Wifi, Fuel, Sparkles, CircleDot, PersonStanding } from 'lucide-react';

// Couleurs des carburants comme à la pompe (normes européennes)
const getFuelColor = (fuel) => {
  const name = (fuel.short_name || fuel.name || '').toLowerCase();
  
  // Gazole / Diesel - Noir avec accent jaune
  if (name.includes('gazole') || name.includes('diesel') || name.includes('b7')) {
    return { bg: 'bg-yellow-400', text: 'text-black', border: 'border-yellow-500' };
  }
  
  // E85 - Jaune/Or
  if (name.includes('e85') || name.includes('ethanol')) {
    return { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-600' };
  }
  
  // E10 - Vert clair
  if (name.includes('e10')) {
    return { bg: 'bg-emerald-400', text: 'text-black', border: 'border-emerald-500' };
  }
  
  // SP95 - Vert
  if (name.includes('sp95') || name.includes('95')) {
    return { bg: 'bg-green-500', text: 'text-white', border: 'border-green-600' };
  }
  
  // SP98 - Vert foncé
  if (name.includes('sp98') || name.includes('98')) {
    return { bg: 'bg-green-600', text: 'text-white', border: 'border-green-700' };
  }
  
  // GPLc - Bleu
  if (name.includes('gpl')) {
    return { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-600' };
  }
  
  // Default - Gris
  return { bg: 'bg-gray-400', text: 'text-white', border: 'border-gray-500' };
};

// Function to get display name for fuel
const getFuelDisplayName = (fuel) => {
  if (fuel.short_name && fuel.short_name.trim()) return fuel.short_name;
  const nameMap = {
    'Gazole': 'Gazole',
    'Super Sans Plomb 95': 'SP95',
    'Super Sans Plomb 98': 'SP98',
    'Super Sans Plomb 95 E10': 'E10',
    'Super Ethanol E85': 'E85',
    'GPLc': 'GPLc'
  };
  return nameMap[fuel.name] || fuel.name;
};

// Map service names to icons - each service has a UNIQUE icon
const getServiceIcon = (service) => {
  const serviceLower = service.toLowerCase();
  
  // Boutique alimentaire - pomme (AVANT gonflage car contient "air")
  if (serviceLower.includes('boutique') && serviceLower.includes('alimentaire') && !serviceLower.includes('non')) return <Apple className="w-3.5 h-3.5" />;
  
  // Boutique non alimentaire - caddie
  if (serviceLower.includes('boutique') && serviceLower.includes('non')) return <ShoppingCart className="w-3.5 h-3.5" />;
  
  // Lavage - goutte d'eau
  if (serviceLower.includes('lavage')) return <Droplets className="w-3.5 h-3.5" />;
  
  // Gonflage - cible (cercles concentriques) - APRÈS alimentaire
  if (serviceLower.includes('gonflage')) return <Target className="w-3.5 h-3.5" />;
  
  // Toilettes - personne debout
  if (serviceLower.includes('toilette') || serviceLower.includes('wc') || serviceLower.includes('sanitaire')) return <PersonStanding className="w-3.5 h-3.5" />;
  
  // Generic boutique
  if (serviceLower.includes('boutique')) return <ShoppingCart className="w-3.5 h-3.5" />;
  
  // Paiement / Carte / DAB
  if (serviceLower.includes('carte') || serviceLower.includes('paiement') || serviceLower.includes('cb') || serviceLower.includes('dab') || serviceLower.includes('distributeur')) return <CreditCard className="w-3.5 h-3.5" />;
  
  // Restauration / Café
  if (serviceLower.includes('restauration') || serviceLower.includes('café')) return <Apple className="w-3.5 h-3.5" />;
  
  // Réparation / Entretien
  if (serviceLower.includes('vidange') || serviceLower.includes('réparation') || serviceLower.includes('entretien')) return <Wrench className="w-3.5 h-3.5" />;
  
  // Parking / Véhicule / Poids lourd
  if (serviceLower.includes('parking') || serviceLower.includes('véhicule') || serviceLower.includes('poids lourd')) return <Car className="w-3.5 h-3.5" />;
  
  // Wifi
  if (serviceLower.includes('wifi') || serviceLower.includes('internet')) return <Wifi className="w-3.5 h-3.5" />;
  
  // Gaz
  if (serviceLower.includes('gaz') || serviceLower.includes('propane') || serviceLower.includes('butane')) return <Fuel className="w-3.5 h-3.5" />;
  
  // Carburant additivé / Additifs
  if (serviceLower.includes('additi')) return <Sparkles className="w-3.5 h-3.5" />;
  
  // Vente / Services divers
  if (serviceLower.includes('vente')) return <ShoppingCart className="w-3.5 h-3.5" />;
  
  // Default
  return <CircleDot className="w-3.5 h-3.5" />;
};

export const StationCard = ({ station, isBestPrice, selectedFuels }) => {
  const [showServices, setShowServices] = useState(false);
  const brandName = station.brand?.name || station.brand?.short_name || 'INDÉPENDANT';
  const streetLine = station.address?.street_line || '';
  const cityLine = station.address?.city_line || '';
  const fullAddress = [streetLine, cityLine].filter(Boolean).join(', ');

  // Filter fuels if selected, otherwise show all available
  const displayFuels = station.fuels?.filter(fuel => {
    if (selectedFuels.length === 0) return true;
    return selectedFuels.includes(fuel.id);
  }) || [];

  // Get the cheapest fuel for display
  const availableFuelsWithPrice = displayFuels.filter(f => f.price && !f.rupture);
  const cheapestFuel = availableFuelsWithPrice.length > 0
    ? availableFuelsWithPrice.reduce((min, f) => f.price < min.price ? f : min, availableFuelsWithPrice[0])
    : null;

  const formatDistance = (meters) => {
    if (!meters) return null;
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const formatPrice = (price) => {
    if (!price) return '—';
    return price.toFixed(3) + ' €';
  };

  return (
    <div 
      data-testid={`station-card-${station.id}`}
      className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 relative overflow-hidden hover:border-[#FF4500] transition-colors"
    >
      {isBestPrice && (
        <div className="best-price-badge" data-testid="best-price-badge">
          Meilleur Prix
        </div>
      )}

      {/* Header */}
      <div className="p-4 pb-2 flex justify-between items-start">
        <h3 
          className="font-heading text-xl font-bold uppercase tracking-tight text-black dark:text-white truncate flex-1 pr-2"
          title={brandName}
        >
          {brandName}
        </h3>
        {station.distance && (
          <span 
            className="bg-black dark:bg-white text-white dark:text-black text-xs font-mono px-2 py-1 flex-shrink-0"
            data-testid="station-distance"
          >
            {formatDistance(station.distance)}
          </span>
        )}
      </div>

      {/* Address */}
      <div className="px-4 pb-3">
        <p className="text-gray-600 dark:text-gray-400 text-sm flex items-start gap-1 truncate" title={fullAddress}>
          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="truncate">{fullAddress || 'Adresse non disponible'}</span>
        </p>
      </div>

      {/* Ticket tear effect */}
      <div className="ticket-tear" />

      {/* Fuels List */}
      <div className="p-4 pt-3 space-y-3">
        {displayFuels.length > 0 ? (
          displayFuels.map((fuel) => {
            const fuelColor = getFuelColor(fuel);
            return (
              <div 
                key={fuel.id}
                className="flex justify-between items-center gap-3"
                data-testid={`fuel-price-${station.id}-${fuel.id}`}
              >
                {/* Fuel badge with color */}
                <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${fuelColor.bg} ${fuelColor.text} border-2 ${fuelColor.border} min-w-[70px] text-center`}>
                  {getFuelDisplayName(fuel)}
                </span>
                
                {fuel.rupture ? (
                  <span className="text-sm font-bold text-red-600 uppercase">Rupture</span>
                ) : (
                  <span className={`price-display text-2xl ${
                    cheapestFuel && fuel.id === cheapestFuel.id ? 'text-[#FF4500]' : 'text-black dark:text-white'
                  }`}>
                    {formatPrice(fuel.price)}
                  </span>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-2">
            Aucun carburant disponible
          </p>
        )}
      </div>

      {/* Services section */}
      {station.services && station.services.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowServices(!showServices)}
            data-testid={`services-toggle-${station.id}`}
            className="w-full px-4 py-2 flex items-center justify-between text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-2">
              <Wrench className="w-3.5 h-3.5" />
              {station.services.length} service{station.services.length > 1 ? 's' : ''}
            </span>
            {showServices ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          
          {showServices && (
            <div className="px-4 pb-3 pt-1">
              <div className="flex flex-wrap gap-2">
                {station.services.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-none border border-gray-300 dark:border-gray-600"
                    data-testid={`service-${station.id}-${index}`}
                  >
                    {getServiceIcon(service)}
                    {service}
                  </span>
                ))}
              </div>
              
              {/* 24/7 indicator */}
              {station.hours?.automate_24_7 && (
                <div className="mt-2 flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-bold uppercase">Automate 24h/24</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      {station.last_update && (
        <div className="px-4 pb-3 flex items-center gap-1 text-gray-500 dark:text-gray-400">
          <Clock className="w-3 h-3" />
          <span className="text-xs font-mono">
            Mise à jour: {station.last_update}
          </span>
        </div>
      )}
    </div>
  );
};
